import React from "react";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { setCheckoutStep, setSubscription } from "@store/reducers/cart";

import styled from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";
import { computeSubscriptionPricing } from "@/utils/pricing";

import {
  ButtonGroup,
  IconCircle,
  PrimaryBtn,
  SectionLabel,
  SlidePanelBody,
  SlidePanelFooter,
  StepHeaderWrap,
  StepSubtitle,
  StepTitle,
} from "./styles";

/* The first section label sits right under the fixed header — on phone trim its
   top margin so "Billing Period" hugs the header (the shared SectionLabel's
   18px phone margin is meant for between-section gaps, not the very first one). */
const FirstSectionLabel = styled(SectionLabel)`
  @media (max-width: 600px) {
    margin-top: 2px;
  }
`;

const PlanIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

/* PHONE-ONLY fixed header (modal-template-phone.md): heading left, ✕ over its
   right, non-scrolling. Hidden ≥601px so desktop/tablet keep the centred
   in-body header untouched. `data-slide-header` lets CartModal's measure()
   account for it. The `& + body` top-trim is phone-scoped so it never alters
   the desktop body padding. */
const PhoneHeader = styled.div.attrs({ "data-slide-header": "" })`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    /* Symmetric ✕ spacing (modal-template-phone.md): 16px above the ✕ (top:16)
       + 36px ✕ + 16px below = 68px. Side-only padding so centring is even. */
    min-height: 68px;
    padding: 0 24px;

    h2 {
      font-family: "Cormorant Garamond", serif;
      font-size: 1.5rem;
      font-weight: 400;
      color: #fff;
      margin: 0;
      line-height: 1.2;
      text-align: center;
    }

    & + [data-slide-body] {
      padding-top: 0;
    }
  }
`;

/* The original centred icon-circle header — desktop/tablet only (hidden on
   phone, where PhoneHeader takes over). */
const DesktopHeader = styled(StepHeaderWrap)`
  @media (max-width: 600px) {
    display: none;
  }
`;

/* Monthly/Annual billing toggle. Active = filled blue pill, inactive =
   transparent with gold border. Inactive hover lifts the border + adds
   a faint blue wash so the non-selected option signals it's clickable.
   Inline styles can't express `:hover`, hence styled-components. */
const BillingBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  background: ${({ $active }) => ($active ? "rgb(156, 211, 253)" : "transparent")};
  border: 1.5px solid ${({ $active }) => ($active ? "rgb(156, 211, 253)" : "rgba(255, 255, 255, 0.2)")};
  color: ${({ $active }) => ($active ? "rgb(97, 84, 73)" : "#fff")};

  &:hover {
    ${({ $active }) =>
      $active
        ? ""
        : `
          background: rgba(156, 211, 253, 0.12);
          border-color: rgba(156, 211, 253, 0.6);
        `}
  }
`;

/* Stepper +/− circles. Inline styles can't express `:hover`, so the
   button itself is a styled-component — hover swaps the bg to a brighter
   shade, no transform / no movement. */
const StepperBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgb(156, 211, 253);
  color: rgb(97, 84, 73);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;

  svg { display: block; width: 18px; height: 18px; }

  /* Hover-tint only on real pointers — on touch (phones/tablets) iOS leaves
     :hover STUCK after a tap, so the button would keep the brighter bg until
     you tap elsewhere. Gating behind (hover: hover) undoes that for touch. */
  @media (hover: hover) {
    &:hover {
      background: #c7e4fb;
      color: #3c2d23;
    }
  }

  /* phone: bigger tap target so fingers can press +/- reliably */
  @media (max-width: 600px) {
    width: 36px;
    height: 36px;
    svg { width: 20px; height: 20px; }
  }
`;

/* Row: label + controls. Desktop = label left, controls right. Phone drops the
   label entirely (the value carries the full description) and the controls span
   the row so −/value/+ fill the width with no squish. */
const StepperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(235, 194, 142, 0.25);
  border-radius: 12px;

  @media (max-width: 600px) {
    padding: 12px 14px;
  }
`;

/* The full descriptive text lives in the value; no separate label (matches the
   mobile layout at all widths — −/value/+ span the row). */
const StepperLabel = styled.span`
  display: none;
`;

const StepperControls = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: space-between;
  gap: 10px;
`;

const StepperValue = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fff;
  flex: 1;
  min-width: 0;
  text-align: center;
`;

const Stepper: React.FC<{
  label: string;
  value: string;
  valuePhone: string;
  onMinus: () => void;
  onPlus: () => void;
  hideMinus?: boolean;
}> = ({ label, valuePhone, onMinus, onPlus, hideMinus }) => (
  <StepperRow>
    <StepperLabel>{label}</StepperLabel>
    <StepperControls>
      <StepperBtn onClick={onMinus} style={{ visibility: hideMinus ? "hidden" : "visible" }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </StepperBtn>
      <StepperValue>{valuePhone}</StepperValue>
      <StepperBtn onClick={onPlus}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </StepperBtn>
    </StepperControls>
  </StepperRow>
);

const PlanConfigView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { subscription } = useAppSelector((state) => state.cart);
  const subConfig = useSubscriptionConfig();

  const { channels, claims, billingPeriod } = subscription;
  const isAnnual = billingPeriod === "annual";

  // Centralized pricing (claims billed per channel) — mirrors the backend.
  const pricing = computeSubscriptionPricing(subConfig, { channels, claims, isAnnual });
  const premiumBase = pricing.baseMonthly;
  const extraChannels = pricing.extraChannels;
  const extraClaimPacks = pricing.extraClaimPacks;
  const addonsPrice = pricing.addonsMonthly;
  // Round to cents at each aggregation so 26.10 × 12 doesn't surface as
  // 313.20000000000005 (JS float quirk).
  const monthlyTotal = Math.round((premiumBase + addonsPrice) * 100) / 100;
  const annualTotal = Math.round(monthlyTotal * 12 * 100) / 100;

  return (
    <>
      {/* PHONE: fixed header — centred title, no icon (✕ stays top-right). */}
      <PhoneHeader>
        <h2>Configure Your Plan</h2>
      </PhoneHeader>

      <SlidePanelBody>
      {/* DESKTOP/TABLET: original centred header (hidden on phone). */}
      <DesktopHeader>
        <IconCircle><PlanIcon /></IconCircle>
        <div>
          <StepTitle>Configure Your Plan</StepTitle>
          <StepSubtitle>Choose your billing period and customize your subscription</StepSubtitle>
        </div>
      </DesktopHeader>

      {/* Billing toggle */}
      <FirstSectionLabel><span>Billing Period</span></FirstSectionLabel>
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
        <BillingBtn $active={!isAnnual} onClick={() => dispatch(setSubscription({ billingPeriod: "monthly" }))}>
          Monthly
        </BillingBtn>
        <BillingBtn $active={isAnnual} onClick={() => dispatch(setSubscription({ billingPeriod: "annual" }))}>
          Annual (-{subConfig.annualDiscountPercent}%)
        </BillingBtn>
      </div>

      {/* Steppers */}
      <SectionLabel><span>Your Configuration</span></SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "24px" }}>
        <Stepper
          label="YouTube Channels"
          value={`${channels} ${channels === 1 ? "channel" : "channels"}`}
          valuePhone={`${channels} YouTube ${channels === 1 ? "Channel" : "Channels"}`}
          onMinus={() => dispatch(setSubscription({ channels: Math.max(subConfig.includedChannels, channels - 1) }))}
          onPlus={() => dispatch(setSubscription({ channels: channels + 1 }))}
          hideMinus={channels <= subConfig.includedChannels}
        />
        <Stepper
          label="Monthly Claims per Channel"
          value={`${claims} claims`}
          valuePhone={`${claims} monthly claims per channel`}
          onMinus={() => dispatch(setSubscription({ claims: Math.max(subConfig.includedClaims, claims - subConfig.claimIncrement) }))}
          onPlus={() => dispatch(setSubscription({ claims: Math.min(200, claims + subConfig.claimIncrement) }))}
          hideMinus={claims <= subConfig.includedClaims}
        />
      </div>

      {/* Price breakdown */}
      <SectionLabel><span>Price Breakdown</span></SectionLabel>
      <div style={{
        background: "rgba(255, 255, 255, 0.04)", border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: "12px", padding: "16px", marginBottom: 0,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
        fontSize: "0.875rem",
      }}>
        {/* On annual + base config the breakdown collapses to just the
            Total row (which already shows `$X/year ($Y/mo)`). On monthly
            mode the "Subscription" row is always the only breakdown line.
            Addons reintroduce the per-line split in both modes. */}
        {(() => {
          const hasAddons = extraChannels > 0 || extraClaimPacks > 0;
          const showSubscriptionRow = hasAddons || !isAnnual;
          return (
            <>
              {showSubscriptionRow && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "rgba(251, 244, 236, 0.8)" }}>
                  <span>Base Subscription ({subConfig.includedChannels} {subConfig.includedChannels === 1 ? "channel" : "channels"} / {subConfig.includedClaims} claims)</span>
                  <span>${premiumBase.toFixed(2)}/mo</span>
                </div>
              )}
              {extraChannels > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "rgba(251, 244, 236, 0.8)" }}>
                  <span>+ {extraChannels} additional {extraChannels === 1 ? "Channel" : "Channels"}</span>
                  <span>${pricing.channelAddonMonthly.toFixed(2)}/mo</span>
                </div>
              )}
              {extraClaimPacks > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "rgba(251, 244, 236, 0.8)" }}>
                  <span>+ {pricing.extraClaimsPerChannel} claims per channel{channels > 1 ? ` × ${channels}` : ""}</span>
                  <span>${pricing.claimsAddonMonthly.toFixed(2)}/mo</span>
                </div>
              )}
            </>
          );
        })()}
        {isAnnual && (() => {
          const hasAddons = extraChannels > 0 || extraClaimPacks > 0;
          return (
            <div style={{ borderTop: hasAddons ? "1px solid rgba(255, 255, 255, 0.1)" : "none", paddingTop: hasAddons ? "10px" : 0, marginTop: "4px", display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1.25rem", color: "#fff" }}>
              <span>Monthly</span>
              <span>${monthlyTotal.toFixed(2)}/mo</span>
            </div>
          );
        })()}
        <div style={{ borderTop: isAnnual ? "none" : "1px solid rgba(255, 255, 255, 0.1)", paddingTop: isAnnual ? 0 : "10px", marginTop: isAnnual ? "0px" : "4px", display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1.25rem", color: "#9cd3fd" }}>
          <span>Total</span>
          <span>{isAnnual ? `$${annualTotal.toFixed(2)}/year` : `$${monthlyTotal.toFixed(2)}/mo`}</span>
        </div>
        {isAnnual && (
          <div style={{ textAlign: "right", fontSize: "0.8125rem", color: "rgba(251, 244, 236, 0.5)", marginTop: "4px" }}>
            ${annualTotal.toFixed(2)} charged upfront ({subConfig.annualDiscountPercent}% saved)
          </div>
        )}
      </div>
      </SlidePanelBody>

      <SlidePanelFooter style={{ padding: "8px 24px 24px" }}>
        <ButtonGroup style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
          {/* Plan-config is the first step of the subscription flow (Cart is
              empty in subscription mode, Checkout is skipped for authenticated
              users). For guests still on the flow, the only "back" target is
              the login/signup gate they just came from — they can use the
              modal's close button instead. */}
          <PrimaryBtn onClick={() => dispatch(setCheckoutStep("sub-review"))} style={{ width: "100%" }}><ButtonLabel glyph="arrowRight">Continue</ButtonLabel></PrimaryBtn>
        </ButtonGroup>
      </SlidePanelFooter>
    </>
  );
};

export default PlanConfigView;
